/* eslint-disable lingui/no-unlocalized-strings */

import { CaretDown } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { create } from "zustand";

import { useIsCoachOptimizing } from "@/client/stores/coach-optimization";
import { useResumeStore } from "@/client/stores/resume";

/**
 * Collapsing the builder's sections, but ONLY during a coach optimization.
 *
 * WHY: in coach mode the sidebar has two jobs competing for the same column — the AI's suggestions
 * and the full editing UI for fifteen sections. Expanded, the suggestion cards are separated by
 * screens of item lists, drag handles and "Add a new item" buttons, so a student cannot see what the
 * AI is proposing without scrolling past everything they are not being asked to change.
 *
 * Collapsed by default inverts that: the sidebar becomes a list of section headers with their
 * "AI Suggested (n)" counts and their suggestion cards, and the editing UI is one click away for a
 * student who wants to make a change by hand.
 *
 * ⚠️ THE SUGGESTIONS STAY VISIBLE WHEN COLLAPSED. Only the section's own editor is hidden. Collapsing
 * the suggestions too would hide the thing the screen exists to show.
 *
 * ⚠️ NO EFFECT OUTSIDE A COACH SESSION. `useCoachSectionBody` returns `visible: true` and
 * `collapsible: false` in the normal builder, so every section renders exactly as it does today.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */

type CoachSectionCollapseState = {
  /** sectionId → expanded. Absent means collapsed, so every section starts closed. */
  expanded: Record<string, boolean>;
  toggle: (sectionId: string) => void;
};

/**
 * Module-level rather than component state, because `SectionBase` remounts on section reorder and on
 * the AnimatePresence transitions — local state would silently re-collapse a section the student had
 * just opened. Not persisted: "collapsed by default" is per visit to the screen.
 */
const useCoachSectionCollapseStore = create<CoachSectionCollapseState>()((set) => ({
  expanded: {},
  toggle: (sectionId) =>
    set((state) => ({
      expanded: { ...state.expanded, [sectionId]: !state.expanded[sectionId] },
    })),
}));

/**
 * Whether a section should render its own editing UI, and whether it can be collapsed at all.
 *
 * @returns `collapsible` false in the normal builder — callers must not render a toggle then.
 */
export const useCoachSectionBody = (sectionId: string) => {
  const resumeId = useResumeStore((state) => state.resume.id);
  const isCoachOptimizing = useIsCoachOptimizing(resumeId);
  const expanded = useCoachSectionCollapseStore((state) => Boolean(state.expanded[sectionId]));

  return {
    collapsible: isCoachOptimizing,
    // Outside a coach session nothing is ever hidden.
    visible: !isCoachOptimizing || expanded,
    expanded,
  };
};

/**
 * Caret that expands a section's editor during a coach optimization. Renders nothing otherwise.
 *
 * Deliberately a small affordance next to the section options rather than making the whole header a
 * button: the header also carries `SectionOptions`, and nesting an interactive menu inside a
 * clickable region makes both harder to hit.
 */
export const CoachSectionCollapseToggle = ({ sectionId }: { sectionId: string }) => {
  const { collapsible, expanded } = useCoachSectionBody(sectionId);
  const toggle = useCoachSectionCollapseStore((state) => state.toggle);

  if (!collapsible) return null;

  return (
    <button
      type="button"
      aria-expanded={expanded}
      aria-label={expanded ? "Hide section editor" : "Show section editor"}
      className="flex size-7 shrink-0 items-center justify-center rounded-full text-foreground/50 transition-colors hover:bg-secondary-accent hover:text-foreground"
      onClick={() => {
        toggle(sectionId);
      }}
    >
      <CaretDown
        size={14}
        className={cn("transition-transform duration-200", expanded && "rotate-180")}
      />
    </button>
  );
};
